import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => {
  return localStorage.getItem('token');
};

const vectorApiClient = axios.create({
  baseURL: config.api.vectorServiceUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 600000 // 10 minutes - increased for large document processing with chunking
});

vectorApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(vectorApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(vectorApiClient);

const vectorService = {
  // Fetch vectors from the vector service. Optional `filepath` query param is supported.
  fetchVectors: async (filepath, limit = null, offset = null, fileId = null) => {
    try {
      const params = {};
      if (fileId) params.file_id = fileId;
      else if (filepath) params.filepath = filepath;
      if (limit !== null) params.limit = limit;
      if (offset !== null) params.offset = offset;
      const response = await vectorApiClient.get('vectors', { params });
      return response.data; // expected shape: { success, vectors: [...], total_count, offset, limit }
    } catch (error) {
      console.error('Error fetching vectors:', error);
      throw error;
    }
  },

  // Analyze a vector's text content for signal/noise classification
  analyzeVector: async (vectorId, text) => {
    try {
      const response = await vectorApiClient.post('vectors/analyze', {
        vector_id: vectorId,
        text: text
      });
      return response.data; // expected shape: { success, vector_id, analysis: {...}, error }
    } catch (error) {
      console.error('Error analyzing vector:', error);
      throw error;
    }
  }
  ,

  // Submit a manual review/analysis for a vector
  submitReview: async (vectorId, analysis) => {
    try {
      const response = await vectorApiClient.post('vectors/review', {
        vector_id: vectorId,
        analysis: analysis
      });
      return response.data; // expected { success, vector_id, analysis }
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  }
  ,

  // Ask the vector service to compute a high-level purpose from a list of analysis "what" strings
  getVectorsPurpose: async (whats) => {
    try {
      const response = await vectorApiClient.post('vectors/purpose', { whats });
      return response.data; // expected { success, purpose }
    } catch (error) {
      console.error('Error fetching vectors purpose:', error);
      throw error;
    }
  },

  // Ask the vector service to compute a document-level purpose given an s3 key
  // Returns immediately with job_id (202 status) for polling
  getDocumentPurpose: async (s3Key) => {
    try {
      const response = await vectorApiClient.post('vectors/documents/purpose', { s3_key: s3Key });
      
      // Check if this is an async response (202 with job_id)
      if (response.status === 202 && response.data.job_id) {
        return {
          success: true,
          job_id: response.data.job_id,
          status: response.data.status || 'pending',
          message: response.data.message || 'Document purpose analysis job started'
        };
      }
      
      // Synchronous response (backwards compatibility)
      return response.data; // expected { success, purpose }
    } catch (error) {
      console.error('Error fetching document purpose:', error);
      throw error;
    }
  },

  // Poll for document purpose job status
  pollDocumentPurposeJob: async (jobId) => {
    try {
      const response = await vectorApiClient.get(`vectors/documents/purpose/job/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error polling document purpose job:', error);
      throw error;
    }
  },

  // Ask the vector service to compute a document summary given an s3 key
  // Returns immediately with job_id (202 status) for polling
  getDocumentSummary: async (s3Key, category = null, promptOverride = null) => {
    try {
      const payload = {
        s3_key: s3Key
      };

      if (promptOverride != null) {
        payload.promptOverride = promptOverride;
      }

      const summaryResponse = await vectorApiClient.post('vectors/documents/summary', payload);
      
      // Check if this is an async response (202 with job_id)
      if (summaryResponse.status === 202 && summaryResponse.data.job_id) {
        return {
          success: true,
          job_id: summaryResponse.data.job_id,
          status: summaryResponse.data.status,
          message: summaryResponse.data.message
        };
      }
      
      // Synchronous response (backwards compatibility)
      return summaryResponse.data; // expected { success, summary }
    } catch (error) {
      console.error('Error fetching document summary:', error);
      throw error;
    }
  },

  // Poll for document summary job status
  pollDocumentSummaryJob: async (jobId) => {
    try {
      const response = await vectorApiClient.get(`vectors/documents/summary/job/${jobId}`);
      return response.data; // expected { job_id, status, summary, error, created_at, updated_at }
    } catch (error) {
      console.error('Error polling document summary job:', error);
      throw error;
    }
  },

  // Fetch file-level attributes from the vector service's file inspector endpoints
  getFile: async (s3Path) => {
    try {
      const params = { s3_path: s3Path };
      const response = await vectorApiClient.get('vectors/files', { params });
      return response.data; // expected { success, file: { s3_path, attributes, ... } }
    } catch (error) {
      console.error('Error fetching file attributes:', error);
      throw error;
    }
  },

  // Fetch aggregated summaries for all KB files in one call
  fetchAllFileSummaries: async (client_id, doc_type, limit = null, offset = null) => {
    try {
      const params = {};
      if (client_id) params.client_id = client_id;
      if (doc_type) params.doc_type = doc_type;
      if (limit !== null) params.limit = limit;
      if (offset !== null) params.offset = offset;
      const response = await vectorApiClient.get('vectors/files/summary', { params });
      return response.data; // expected { success, files: [...], total_count, offset, limit }
    } catch (error) {
      console.error('Error fetching all file summaries:', error);
      throw error;
    }
  },

  // Create a file record (POST /files)
  createFile: async (s3Path, attributes) => {
    try {
      // If caller included doc_summary, edited_document_summary, or client_id inside attributes, lift them to top-level
      const payloadAttrs = { ...(attributes || {}) };
      const payload = { s3_path: s3Path };
      if (payloadAttrs.doc_summary !== undefined) {
        payload.doc_summary = payloadAttrs.doc_summary;
        delete payloadAttrs.doc_summary;
      }
      if (payloadAttrs.edited_document_summary !== undefined) {
        payload.edited_document_summary = payloadAttrs.edited_document_summary;
        delete payloadAttrs.edited_document_summary;
      }
      if (payloadAttrs.client_id !== undefined) {
        payload.client_id = payloadAttrs.client_id;
        delete payloadAttrs.client_id;
      }
      payload.attributes = payloadAttrs;
      const response = await vectorApiClient.post('vectors/files', payload);
      return response.data; // expected { success, file }
    } catch (error) {
      console.error('Error creating file record:', error);
      throw error;
    }
  },

  // Update a file record (PUT /files?s3_path=...)
  updateFile: async (s3Path, attributes) => {
    try {
      // If caller included doc_summary, edited_document_summary, doc_type, sub_type, or account_number inside attributes, lift them to top-level
      const payloadAttrs = { ...(attributes || {}) };
      const payload = {};
      if (payloadAttrs.doc_summary !== undefined) {
        payload.doc_summary = payloadAttrs.doc_summary;
        delete payloadAttrs.doc_summary;
      }
      if (payloadAttrs.edited_document_summary !== undefined) {
        payload.edited_document_summary = payloadAttrs.edited_document_summary;
        delete payloadAttrs.edited_document_summary;
      }
      if (payloadAttrs.doc_type_id !== undefined) {
        payload.doc_type_id = payloadAttrs.doc_type_id;
        delete payloadAttrs.doc_type_id;
      }
      if (payloadAttrs.sub_type_id !== undefined) {
        payload.sub_type_id = payloadAttrs.sub_type_id;
        delete payloadAttrs.sub_type_id;
      }
      if (payloadAttrs.account_number !== undefined) {
        payload.account_number = payloadAttrs.account_number;
        delete payloadAttrs.account_number;
      }
      payload.attributes = payloadAttrs;
      const params = { s3_path: s3Path };
      const response = await vectorApiClient.put('vectors/files', payload, { params });
      return response.data; // expected { success, file }
    } catch (error) {
      console.error('Error updating file record:', error);
      throw error;
    }
  },

  /**
   * Generate and download a PDF from document summary
   * @param {string} s3Path - The S3 path
   * @param {string} summary - The summary content (HTML)
   * @param {string} filename - The filename for the PDF
   * @returns {Promise<Blob>} - Returns a Blob containing the PDF
   */
  async exportSummaryToPdf(s3Path, summary, filename) {
    try {
      const requestBody = {
        s3_path: s3Path,
        summary: summary,
        filename: filename
      };

      // Use arraybuffer responseType to properly handle binary PDF data
      const response = await vectorApiClient.post('/vectors/export-summary-pdf', requestBody, {
        responseType: 'arraybuffer'
      });
      
      // Convert arraybuffer to blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      return blob;
    } catch (error) {
      console.error('Error exporting summary to PDF:', error);
      throw error;
    }
  }
};

export default vectorService;

